/**
 * Client HTTP FacturX.
 * - credentials: cookie session Suite
 * - X-User-Id : repli iframe (même règle que Coffre / Mail)
 * - 401 / 403 : renvoyés au SSO, pas gérés dans chaque page
 */
import { facturxNotifyApiAuthError } from "../initFacturxSuiteAuth";
import { getFacturxApiBase, readUserId } from "../suiteSession";

export class FacturxApiError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(message: string, status: number, code: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

export async function facturxFetch<T>(path: string, options: RequestInit = {}): Promise<T> {
  const headers = new Headers(options.headers);
  const userId = readUserId();
  if (userId) headers.set("X-User-Id", userId);
  if (options.body && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  const res = await fetch(`${getFacturxApiBase()}/${path.replace(/^\//, "")}`, {
    credentials: "include",
    cache: "no-store",
    ...options,
    headers,
  });
  const data = (await res.json().catch(() => ({}))) as {
    ok?: boolean;
    error?: string;
    message?: string;
  };
  if (facturxNotifyApiAuthError(res.status, data.error)) {
    throw new FacturxApiError("Session", res.status, data.error ?? "auth");
  }
  if (!res.ok || data.ok === false) {
    throw new FacturxApiError(data.message || data.error || `HTTP ${res.status}`, res.status, data.error ?? "error");
  }
  return data as T;
}

/** Upload multipart (logo) — ne pas forcer Content-Type JSON. */
export async function facturxUpload<T>(path: string, body: FormData): Promise<T> {
  const headers = new Headers();
  const userId = readUserId();
  if (userId) headers.set("X-User-Id", userId);
  const res = await fetch(`${getFacturxApiBase()}/${path.replace(/^\//, "")}`, {
    method: "POST",
    credentials: "include",
    cache: "no-store",
    headers,
    body,
  });
  const data = (await res.json().catch(() => ({}))) as {
    ok?: boolean;
    error?: string;
    message?: string;
  };
  if (facturxNotifyApiAuthError(res.status, data.error)) {
    throw new FacturxApiError("Session", res.status, data.error ?? "auth");
  }
  if (!res.ok || data.ok === false) {
    throw new FacturxApiError(data.message || data.error || `HTTP ${res.status}`, res.status, data.error ?? "error");
  }
  return data as T;
}

/** Télécharge un binaire (PDF). Ne passe pas par le parse JSON. */
export async function facturxDownload(path: string, fallbackName: string): Promise<void> {
  const headers = new Headers();
  const userId = readUserId();
  if (userId) headers.set("X-User-Id", userId);
  const res = await fetch(`${getFacturxApiBase()}/${path.replace(/^\//, "")}`, {
    credentials: "include",
    cache: "no-store",
    headers,
  });
  const type = res.headers.get("Content-Type") || "";
  if (type.includes("application/json") || !res.ok) {
    const data = (await res.json().catch(() => ({}))) as { error?: string; message?: string };
    if (facturxNotifyApiAuthError(res.status, data.error)) {
      throw new FacturxApiError("Session", res.status, data.error ?? "auth");
    }
    throw new FacturxApiError(data.message || data.error || `HTTP ${res.status}`, res.status, data.error ?? "error");
  }
  const blob = await res.blob();
  const cd = res.headers.get("Content-Disposition") || "";
  const m = cd.match(/filename="([^"]+)"/);
  const name = m?.[1] || fallbackName;
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}
